// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

import { useState, useEffect } from 'react';
import { LOCALE_OPTIONS } from '@vn-grc/shared';
import { apiClient } from '../services/api-client.js';

export interface InstalledLanguage {
  value: string;
  label: string;
}

/** Module-level cache so all consumers share the same data without redundant fetches. */
let cachedLanguages: InstalledLanguage[] | null = null;
let fetchPromise: Promise<InstalledLanguage[]> | null = null;

/**
 * Sprint 26.4.2: Fetch installed language packs from the public API.
 * Falls back to the static LOCALE_OPTIONS if the API is unavailable.
 * Results are cached in-memory for the lifetime of the application.
 */
export function useInstalledLanguages(): {
  languages: InstalledLanguage[];
  loading: boolean;
} {
  const [languages, setLanguages] = useState<InstalledLanguage[]>(
    cachedLanguages ?? LOCALE_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
  );
  const [loading, setLoading] = useState(!cachedLanguages);

  useEffect(() => {
    if (cachedLanguages) {
      setLanguages(cachedLanguages);
      setLoading(false);
      return;
    }

    if (!fetchPromise) {
      fetchPromise = apiClient
        .listInstalledLanguages()
        .then((res) => {
          const data = res.data ?? [];
          if (data.length === 0) {
            // No installed languages — fall back to static list
            return LOCALE_OPTIONS.map((o) => ({ value: o.value, label: o.label }));
          }
          // Sprint 63.32.0 (#543) — each language named in itself ("Tiếng Việt").
          return data.map(
            (lang: { locale: string; display_name: string; native_name?: string }) => ({
              value: lang.locale,
              label: lang.native_name || lang.display_name,
            }),
          );
        })
        .catch(() => {
          // API unavailable — fall back to static list
          return LOCALE_OPTIONS.map((o) => ({ value: o.value, label: o.label }));
        });
    }

    fetchPromise.then((result) => {
      cachedLanguages = result;
      setLanguages(result);
      setLoading(false);
    });
  }, []);

  return { languages, loading };
}

/** Reset cache (useful for testing or after admin changes installed languages). */
export function resetInstalledLanguagesCache(): void {
  cachedLanguages = null;
  fetchPromise = null;
}
